import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/react-hooks";
import { Alert, Spin } from "antd";
import { connect } from "formik";
import { Select } from "formik-antd";
import gql from "fraql";
import { each, isNil } from "lodash";
import get from "lodash/get";
import omit from "lodash/omit";
import { renderOptions } from "../../utils/selectUtils";
import useUpdateEffect from "../../utils/useUpdateEffect";
import FormFieldCheckboxGroup from "./FormFieldCheckboxGroup";
import FormFieldFormItem from "./FormFieldFormItem";
import FormFieldRadio from "./FormFieldRadio";
import FormFieldSelect from "./FormFieldSelect";

const GET_COMPETITIONS = gql`
  query FormFieldRegistrationSportCompetition_GetCompetitionsInReg($RegId: ID!) {
    GetCompetitionsInReg(regId: $RegId) {
      result
    }
  }
`;

export const DISPLAY_MODES = {
  checkboxList: "checkboxList",
  select: "select",
  radio: "radio",
};

function FormFieldRegistrationSportCompetition(props) {
  const {
    name,
    meta,
    disabled,
    formik: { values, setFieldValue },
    mode,
  } = props;

  const displayDefaultLabel = get(meta, "displayDefaultLabel", true);
  const displayMode = get(meta, "displayMode", DISPLAY_MODES.select);

  const formRegistrationIdValue = get(values, meta.formRegistrationIdValue, null);

  const [selectedSport, setSelectedSport] = useState(null);

  useUpdateEffect(() => {
    // Reset this field's value whenever the Registration field's value changes, since the list of Competitions (and the
    // set of valid Competitions) is dependent on the Registration selected, and therefore the current value for this
    // field may no longer be valid for the new value of the Registration field.
    setFieldValue(name, null);
    setSelectedSport(null);
  }, [formRegistrationIdValue]);

  const { loading: competitionsLoading, error: competitionsError, data: competitionsData } = useQuery(
    GET_COMPETITIONS,
    { variables: { RegId: formRegistrationIdValue }, skip: isNil(formRegistrationIdValue) },
  );

  const options = useMemo(() => {
    if (isNil(selectedSport)) {
      return [];
    }

    const sportsList = get(competitionsData, "GetCompetitionsInReg.result", []);
    const temp = sportsList.find(x => x.id === selectedSport);

    if (isNil(temp)) {
      return [];
    }

    const comps = temp.competitions || [];
    const result = [];

    each(comps, function(comp) {
      if (["ALL", "TEAMS"].includes(mode) && comp.isTeamComp) {
        result.push({ label: comp.name, value: comp.id });
      } else if (["ALL", "INDIVIDUALS"].includes(mode) && !comp.isTeamComp) {
        result.push({ label: comp.name, value: comp.id });
      }
    });

    return result;
  }, [selectedSport, competitionsData, mode]);

  const sportOptions = useMemo(() => {
    const sportsList = get(competitionsData, "GetCompetitionsInReg.result", []);
    if (!sportsList || sportsList.length === 0) {
      return [];
    }

    const result = [];

    each(sportsList, function(sport) {
      let comps = sport.competitions || [];
      if (mode === "TEAMS") {
        comps = comps.filter(x => x.isTeamComp);
      } else if (mode === "INDIVIDUALS") {
        comps = comps.filter(x => !x.isTeamComp);
      }

      if (comps.length > 0) {
        result.push({ label: sport.name, value: sport.id });
      }
    });

    return result;
  }, [competitionsData, mode]);

  const handleSportSelected = val => {
    setFieldValue(name, []);
    setSelectedSport(val);
  };

  const renderSportSelect = () => {
    return (
      <>
        <div className="ant-form-item-label">
          <label className="ant-form-item-no-colon">Sport</label>
        </div>

        <Select
          name="SportId"
          value={selectedSport}
          placeholder="Select a sport"
          onChange={handleSportSelected}
          style={{ paddingBottom: "20px" }}
        >
          {renderOptions(sportOptions)}
        </Select>
      </>
    );
  };

  if (!formRegistrationIdValue) {
    return (
      <FormFieldFormItem {...omit(props, ["formik"])} displayDefaultLabel={displayDefaultLabel} displayForInput={false}>
        <Alert
          message="A Competition can be selected after selecting a Registration"
          type="info"
          className="form-field-alert"
        />
      </FormFieldFormItem>
    );
  }

  if (competitionsLoading && !competitionsData) {
    return (
      <FormFieldFormItem {...omit(props, ["formik"])} displayDefaultLabel={displayDefaultLabel} displayForInput={false}>
        <Spin size="small" className="form-field-spin" />
      </FormFieldFormItem>
    );
  }

  if (competitionsError) {
    return (
      <FormFieldFormItem {...omit(props, ["formik"])} displayDefaultLabel={displayDefaultLabel} displayForInput={false}>
        <Alert message="Competitions failed to load" type="error" className="form-field-alert" />
      </FormFieldFormItem>
    );
  }

  if (!competitionsLoading && (!sportOptions || sportOptions.length === 0)) {
    let alertMessage = "There are no Competitions configured for the selected Registration.";
    if (mode === "TEAMS") {
      alertMessage = "There are no Team Competitions configured for the selected Registration.";
    } else if (mode === "INDIVIDUALS") {
      alertMessage = "There are no Individual Competitions configured for the selected Registration.";
    }

    return (
      <FormFieldFormItem {...omit(props, ["formik"])} displayDefaultLabel={displayDefaultLabel} displayForInput={false}>
        <Alert message={alertMessage} type="info" className="form-field-alert" />
      </FormFieldFormItem>
    );
  }

  switch (displayMode) {
    case DISPLAY_MODES.checkboxList: {
      return (
        <>
          {renderSportSelect()}
          <FormFieldCheckboxGroup
            {...omit(props, ["formik"])}
            disabled={disabled || competitionsLoading}
            meta={{
              allowSelectAll: true,
              selectAllLabel: "All Competitions",
              ...meta,
              options,
            }}
          />
        </>
      );
    }
    case DISPLAY_MODES.select: {
      return (
        <>
          {renderSportSelect()}
          <FormFieldSelect
            {...omit(props, ["formik"])}
            disabled={disabled || competitionsLoading}
            meta={{ ...meta, options }}
          />
        </>
      );
    }
    case DISPLAY_MODES.radio: {
      return (
        <>
          {renderSportSelect()}
          <FormFieldRadio
            {...omit(props, ["formik"])}
            disabled={disabled || competitionsLoading}
            meta={{ ...meta, options }}
          />
        </>
      );
    }
    default: {
      console.error("Unsupported display mode", displayMode);
      return null;
    }
  }
}

FormFieldRegistrationSportCompetition.propTypes = {
  name: PropTypes.string.isRequired,
  meta: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  formik: PropTypes.object.isRequired,
  mode: PropTypes.string,
};

FormFieldRegistrationSportCompetition.defaultProps = {
  disabled: false,
  mode: "ALL",
};

export default connect(FormFieldRegistrationSportCompetition);
